import type { ReactNode } from 'react';
import { useId, useState } from 'react';

import type { ColorName } from './buttonClasses';
import type { SelectOption } from './Select';
import type { Size } from './types';
import Avatar from '../Avatar/Avatar';
import { cn } from '../cn';
import { Alerts, Arrows, General, Maps, Media, Shapes, Users } from '../StrokeIcons';
import WaveSpinner from '../WaveSpinner/WaveSpinner';
import Button from './Button';
import FilterPopover from './FilterPopover';
import FilterSelect from './FilterSelect';
import MiddleEllipsis from './MiddleEllipsis';
import Tooltip from './Tooltip';
import { cleanId } from './types';

export type CampaignState =
  | 'not-started'
  | 'active'
  | 'paused'
  | 'finished'
  | 'interrupted'
  | 'action-required'
  | 'responded'
  | 'researching'
  | 'pending-review'
  | 'rejected'
  | 'contact_unavailable';

export type CampaignButtonOwner = {
  id: string;
  name?: string | null;
  profilePicture?: string | null;
};

export type CampaignButtonProps = {
  state?: CampaignState;
  name?: string;
  size?: Size;
  onClick?: () => void;
  onDelete?: () => void;
  owner?: CampaignButtonOwner;
  owners?: CampaignButtonOwner[];
  onChangeOwner?: (userId: string) => void;
  disabled?: boolean;
  isPending?: boolean;
  isDeleting?: boolean;
  error?: string | null;
  className?: string;
  /** Template options shown when no active campaign (state is undefined) */
  templateOptions?: SelectOption[];
  /** Called when user selects a template to enroll */
  onEnroll?: (templateId: string) => void;
  /** Placeholder text for the enrollment dropdown */
  enrollPlaceholder?: string;
  /** Quick-enroll: last used template ID */
  lastUsedTemplateId?: string;
  /** Quick-enroll: last used template name (shown in tooltip) */
  lastUsedTemplateName?: string;
  /** When provided + interrupted, shows dropdown with "Make active" / "Research" */
  onResearch?: () => void;
  /** Max width of enrollment dropdown popover (default: 280px) */
  popoverMaxWidth?: number;
  /**
   * Explicit tooltip text. When set, replaces the auto-generated "Click to
   * pause/resume/restart" tooltip — used by staging callers where the click
   * navigates rather than mutates.
   */
  tooltipOverride?: string;
  /**
   * Skip the hover icon swap. By default the icon flips from the state icon
   * (e.g. magnifier for `researching`) to the action icon (pause/play/refresh)
   * on hover; staging callers turn this off because the click doesn't mutate.
   */
  disableHoverActionIcon?: boolean;
};

function stateToTooltip(state: CampaignState, error?: string | null): string {
  let base: string;
  switch (state) {
    case 'active':
      base = 'Click to pause';
      break;
    case 'not-started':
      base = 'Click to start';
      break;
    case 'researching':
      base = 'Click to pause';
      break;
    case 'paused':
      base = 'Click to resume';
      break;
    case 'interrupted':
      base = 'Click to resume';
      break;
    case 'action-required':
      base = 'Click to resume';
      break;
    case 'pending-review':
      base = 'Click to resume';
      break;
    case 'finished':
      base = 'Click to restart';
      break;
    case 'responded':
      base = 'Click to restart';
      break;
    case 'rejected':
      base = 'Smart campaign rejected - click to restart';
      break;
    case 'contact_unavailable':
      base = 'Contact unavailable - click to restart';
      break;
  }

  if (error) return `${base} (${error})`;
  return base;
}

function stateToColor(state: CampaignState): ColorName {
  switch (state) {
    case 'active':
    case 'not-started':
    case 'researching':
      return 'blue';
    case 'paused':
    case 'pending-review':
      return 'yellow';
    case 'action-required':
      return 'purple';
    case 'interrupted':
      return 'red';
    case 'finished':
    case 'responded':
      return 'green';
    case 'rejected':
      return 'red';
    case 'contact_unavailable':
      return 'gray';
  }
}

function stateToIcon(state: CampaignState): ReactNode {
  switch (state) {
    case 'active':
      return <Media.Play />;
    case 'not-started':
      return <Maps.Rocket />;
    case 'researching':
      return <General.SearchRefraction />;
    case 'paused':
      return <Media.PauseCircle />;
    case 'action-required':
    case 'pending-review':
      return <Alerts.AlertCircle />;
    case 'interrupted':
      return <Alerts.AlertTriangle />;
    case 'finished':
    case 'responded':
      return <General.CheckCircle />;
    case 'rejected':
      return <General.XCircle />;
    case 'contact_unavailable':
      return <Users.SadFace />;
  }
}

/** Icon showing what action clicking will perform */
function stateToActionIcon(state: CampaignState): ReactNode {
  switch (state) {
    case 'active':
    case 'researching':
      return <Media.PauseCircle />; // Click to pause
    case 'not-started':
      return <Media.Play />; // Click to start
    case 'paused':
    case 'interrupted':
    case 'action-required':
    case 'pending-review':
      return <Media.Play />; // Click to resume
    case 'finished':
    case 'responded':
    case 'rejected':
    case 'contact_unavailable':
      return <Arrows.RefreshCcw05 />; // Click to restart
  }
}

function avatarSizeClass(size: Size = 'md') {
  return {
    '3xs': 'size-5',
    '2xs': 'size-6',
    xs: 'size-7',
    sm: 'size-9',
    md: 'size-10',
    lg: 'size-11',
    xl: 'size-14',
    '2xl': 'size-18',
    '3xl': 'size-22',
    '4xl': 'size-26',
  }[size];
}

function avatarOverflowMR(size: Size = 'md') {
  // margin-right = avatar_width / 2 to make space for the absolutely positioned avatar
  return {
    '3xs': 'mr-2.5', // 10px
    '2xs': 'mr-3', // 12px
    xs: 'mr-3.5', // 14px
    sm: 'mr-[18px]', // 18px
    md: 'mr-5', // 20px
    lg: 'mr-[22px]', // 22px
    xl: 'mr-7', // 28px
    '2xl': 'mr-9', // 36px
    '3xl': 'mr-11', // 44px
    '4xl': 'mr-[52px]', // 52px
  }[size];
}

function avatarTextClass(size: Size = 'md') {
  return {
    '3xs': 'text-[6px]',
    '2xs': 'text-[7px]',
    xs: 'text-[8px]',
    sm: 'text-[10px]',
    md: 'text-xs',
    lg: 'text-sm',
    xl: 'text-base',
    '2xl': 'text-lg',
    '3xl': 'text-xl',
    '4xl': 'text-2xl',
  }[size];
}

function sizeAvatarOverlapPaddingR(size: Size = 'md') {
  // padding = avatar_half + innerGap → visible gap between text and avatar edge matches icon-to-text gap
  return {
    '3xs': 'pr-3', // 10 + 2 = 12
    '2xs': 'pr-3.5', // 12 + 2 = 14
    xs: 'pr-[18px]', // 14 + 4 = 18
    sm: 'pr-[23px]', // 18 + 5 = 23
    md: 'pr-[26px]', // 20 + 6 = 26
    lg: 'pr-[29px]', // 22 + 7 = 29
    xl: 'pr-9', // 28 + 8 = 36
    '2xl': 'pr-[46px]', // 36 + 10 = 46
    '3xl': 'pr-14', // 44 + 12 = 56
    '4xl': 'pr-[66px]', // 52 + 14 = 66
  }[size];
}

function dropdownAvatarSizeClass(size: Size = 'md') {
  return {
    '3xs': 'size-4',
    '2xs': 'size-4',
    xs: 'size-5',
    sm: 'size-6',
    md: 'size-7',
    lg: 'size-8',
    xl: 'size-9',
    '2xl': 'size-10',
    '3xl': 'size-12',
    '4xl': 'size-14',
  }[size];
}

export default function CampaignButton({
  state,
  name,
  size = 'md',
  onClick,
  onDelete,
  owner,
  owners,
  onChangeOwner,
  disabled,
  isPending,
  isDeleting,
  error,
  className,
  templateOptions,
  onEnroll,
  enrollPlaceholder = 'Enroll in campaign',
  lastUsedTemplateId,
  lastUsedTemplateName,
  onResearch,
  popoverMaxWidth = 280,
  tooltipOverride,
  disableHoverActionIcon,
}: CampaignButtonProps) {
  const id = useId();
  const avatarAnchorName = `--campaign-avatar-${cleanId(id)}`;
  const popoverId = `campaign-owner-popover-${cleanId(id)}`;
  const actionPopoverId = `campaign-action-popover-${cleanId(id)}`;
  const actionAnchorName = `--campaign-action-${cleanId(id)}`;
  const [isOpen, setIsOpen] = useState(false);

  // No active campaign — show enrollment dropdown
  if (!state) {
    return (
      <div className={cn('flex items-center gap-1 min-w-0 max-w-full', className)}>
        {lastUsedTemplateId && onEnroll && (
          <Button
            size={size}
            color="blue"
            shape="pill"
            text
            bordered={false}
            icon={<Shapes.Cube02 />}
            tooltip={`Enroll: ${lastUsedTemplateName ?? 'Last used'}`}
            onClick={() => onEnroll(lastUsedTemplateId)}
          />
        )}
        <FilterSelect
          size={size}
          shape="pill"
          options={templateOptions ?? []}
          onChange={onEnroll}
          placeholder={enrollPlaceholder}
          color="blue"
          autoWidth
          popoverMaxWidth={popoverMaxWidth}
        />
      </div>
    );
  }

  const ownerItems =
    owners?.map((o) => ({
      value: o.id,
      label: o.name || 'Unknown',
      icon: (
        <Avatar
          alt={o.name || '?'}
          srcs={[o.profilePicture]}
          shape="circle"
          coloredBackground
          className={cn(dropdownAvatarSizeClass(size), 'text-3xs shrink-0')}
        />
      ),
    })) ?? [];

  const color = stateToColor(state);
  // Icon swaps from state icon to action icon on hover, unless the caller
  // (e.g. a staging badge whose click navigates instead of mutates) opted out.
  const icon = isPending ? (
    <WaveSpinner className="size-full" />
  ) : disableHoverActionIcon ? (
    <span className="size-[1em] flex items-center justify-center">{stateToIcon(state)}</span>
  ) : (
    <span className="relative size-[1em] flex items-center justify-center">
      <span className="absolute inset-0 flex items-center justify-center group-hover:opacity-0 transition-opacity duration-150">
        {stateToIcon(state)}
      </span>
      <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150">
        {stateToActionIcon(state)}
      </span>
    </span>
  );
  const hasOwnerDropdown = owners && owners.length > 0 && onChangeOwner;
  const hasActionDropdown = (state === 'interrupted' || state === 'paused' || state === 'pending-review') && onResearch;

  const actionItems = hasActionDropdown
    ? [
        { value: 'active', label: 'Make active', icon: <Media.Play />, color: 'blue' as const },
        { value: 'research', label: 'Research', icon: <General.SearchRefraction />, color: 'blue' as const },
      ]
    : [];

  return (
    <div className={cn('flex items-center gap-1 min-w-0 max-w-full', className)}>
      {/* Button + Avatar wrapper - relative so avatar can be absolutely positioned */}
      <div className={cn('relative min-w-0', avatarOverflowMR(size))}>
        {/* Main button — wrapped in anchor span when action dropdown is active so
            the Button keeps its own internal anchorName for tooltip positioning */}
        <span
          className="block min-w-0 overflow-hidden"
          style={hasActionDropdown ? ({ anchorName: actionAnchorName } as React.CSSProperties) : undefined}>
          <Button
            size={size}
            color={color}
            icon={icon}
            disabled={disabled}
            {...(hasActionDropdown ? { popoverTarget: actionPopoverId } : { onClick })}
            tooltip={tooltipOverride ?? stateToTooltip(state, error)}
            className={cn(
              'group w-full min-w-0 rounded-tl-full rounded-bl-full rounded-tr-none rounded-br-none font-semibold tracking-[-0.02em]',
              sizeAvatarOverlapPaddingR(size),
            )}>
            {name && <MiddleEllipsis text={name} />}
          </Button>
        </span>

        {/* Avatar / Owner button - absolutely positioned to overlap button edge */}
        <Tooltip
          as="button"
          type="button"
          tooltip={owner?.name || 'Unknown owner'}
          disabled={disabled}
          {...(hasOwnerDropdown ? { popoverTarget: popoverId } : {})}
          style={{ anchorName: avatarAnchorName } as React.CSSProperties}
          className={cn(
            'absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2',
            'rounded-full transition-all duration-150 cursor-pointer',
            'shadow-md outline-solid outline-2 outline-black/20',
            !disabled && 'hover:shadow-lg hover:scale-110',
            isOpen && 'shadow-lg scale-110',
            disabled && 'cursor-not-allowed opacity-50',
          )}>
          <Avatar
            alt={owner?.name || '?'}
            srcs={[owner?.profilePicture]}
            shape="circle"
            coloredBackground
            className={cn(avatarSizeClass(size), avatarTextClass(size))}
          />
        </Tooltip>
      </div>

      {/* Action dropdown popover (interrupted + smart) */}
      {hasActionDropdown && (
        <FilterPopover
          popoverId={actionPopoverId}
          anchorName={actionAnchorName}
          size={size}
          color={color}
          items={actionItems}
          onSelect={(val) => {
            if (val === 'active') onClick?.();
            else if (val === 'research') onResearch?.();
          }}
          popoverStyle={{ minWidth: '150px' }}
          filterable={false}
        />
      )}

      {/* Owner dropdown popover */}
      {hasOwnerDropdown && (
        <FilterPopover
          popoverId={popoverId}
          anchorName={avatarAnchorName}
          size={size}
          color={color}
          items={ownerItems}
          selectedValue={owner?.id}
          onSelect={(val) => onChangeOwner?.(val)}
          onOpenChange={setIsOpen}
          popoverStyle={{ minWidth: '180px' }}
          iconClassName=""
        />
      )}

      {/* Delete button */}
      {onDelete && (
        <Button
          size={size}
          color="red"
          text
          icon={isDeleting ? <WaveSpinner className="size-full" /> : <General.Trash01 />}
          disabled={disabled || isDeleting}
          onClick={onDelete}
          shape="pill"
          bordered={false}
          tooltip="Remove from campaign"
        />
      )}
    </div>
  );
}
